import { uniq } from 'lodash';

import { LOCAL_STORAGE_KEYS } from './constants';
import { CourseName } from '../components/admin/GeneralReport/TableSummaryReport/TableSummaryReport.types';
import { buildTeamHierarchy } from './functions';

/**
 *  Get course ids for courses associated with the teams passed in
 * @param teams - list of teams
 * @param teamIds - list of selected team ids
 * @returns list of course ids
 */
export const getCourseIdsForSelectedTeams = (
  teams: Optional<ITeam[]>,
  teamIds: string[],
): string[] => {
  if (!teams) {
    return [];
  }

  const courseIds: string[] = [];
  const activeTeams = teams.filter((team) => teamIds.includes(team._id));

  activeTeams.forEach((team: ITeam) => {
    if (!team?.courses) {
      return;
    }

    courseIds.push(...(team.courses as string[]));
  });

  return uniq(courseIds);
};

/**
 * Get course ids for CourseName objects passed in
 * @param courseNames - list of CourseName objects
 * @returns list of course ids
 */
export const getCourseIdsFromCourseNames = (courseNames: Optional<CourseName[]>): string[] => {
  return courseNames?.map((course) => course._id) || [];
};

/**
 * Toggle selection of an item in a list
 * @param items - list of items
 * @param item - item to toggle
 * @returns updated list of items
 * @example
 * const items = ['a', 'b', 'c'];
 * const item = 'b';
 * const updatedItems = toggleSelection(items, item);
 * console.log(updatedItems); // ['a', 'c']
 * @example
 * const items = ['a', 'c'];
 * const item = 'b';
 * const updatedItems = toggleSelection(items, item);
 * console.log(updatedItems); // ['a', 'c', 'b']
 */
export const toggleSelection = (items: string[], item: string) => {
  return items.includes(item)
    ? items.filter((id) => id !== item) // Remove item from items
    : [...items, item]; // Add item to items
};

/**
 * Custom sort function for sorting strings case-insensitively
 * @param rowA - row A
 * @param rowB - row B
 * @param columnId - column id
 * @returns -1 if rowA should come before rowB, 1 if rowA should come after rowB, 0 if they are equal
 */
export const customStringSortCaseInsensitive = (rowA: any, rowB: any, columnId: string) => {
  const valueA = rowA.values[columnId].toLowerCase();
  const valueB = rowB.values[columnId].toLowerCase();

  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;

  return 0;
};

/**
 * Determine if the search bar should be shown based on the tree data. The search bar should be shown if there is more than one team or if there is one team with children.
 * @param treeData
 * @returns true if the search bar should be shown, false otherwise
 */
export const showSearchBar = (treeData: ITeam[]) => {
  return treeData?.length > 1 || (treeData?.length === 1 && treeData[0].children?.length);
};

/**
 * Get team manager active teams from user object. Otherwise return an empty array.
 * @param user - The authenticated user
 * @param isTeamManager - Whether the user is a team manager
 * @returns list of active teams
 */
export const getActiveTeams = (user?: IUser, isTeamManager?: boolean): string[] => {
  return isTeamManager ? user?.reportTeams || [] : [];
};

/**
 * Get team manager active deselected courses from user object. Otherwise return an empty array.
 * @param user - The authenticated user
 * @param isTeamManager - Whether the user is a team manager
 * @returns list of active deselected courses
 */
export const getActiveDeselectedCourses = (user?: IUser, isTeamManager?: boolean): string[] => {
  return isTeamManager ? user?.reportDeselectedCourses || [] : [];
};

/**
 * Check if there are multiple selected courses.
 * @param courses - List of CourseName objects.
 * @param deselectedCourses - List of deselected course IDs.
 * @returns True if there are multiple selected courses, false otherwise.
 */
export const hasMultipleSelectedCourses = (
  courses?: CourseName[],
  deselectedCourses?: string[],
) => {
  if (!courses || !deselectedCourses) {
    return false;
  }

  const activeCourses = courses.filter((course) => !deselectedCourses.includes(course._id));

  return activeCourses.length > 1;
};

/**
 * Check if the user has Ontra courses.
 * @param courseNames - List of course names.
 * @returns True if the user has Ontra courses, false otherwise.
 */
export const hasOntraCourse = (courseNames?: string[]): boolean => {
  return courseNames?.some((name) => /\bontra\b/i.test(name)) || false;
};

/**
 * Check if the course is an Ontra course.
 * @param courseName - The name of the course.
 * @returns True if the course is an Ontra course, false otherwise.
 */
export const isOntraCourse = (courseName?: string): boolean => {
  return courseName ? /\bontra\b/i.test(courseName) : false;
};

/**
 * Format a number as a percentage string with two decimal places.
 * @param value - The number to format.
 * @returns The formatted percentage string, or an empty string if the value is not a number.
 */
export const formatPercentValue = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || isNaN(value)) return '';

  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

/**
 * Determines whether any team in the provided array has children.
 *
 * @param teams - An array of team objects, each potentially containing a `children` property.
 * @returns `true` if at least one team has a non-empty `children` array; otherwise, `false`.
 */
export const anyTeamHasChildren = (teams: any[]): boolean => {
  return teams.some((team) => Array.isArray(team.children) && team.children.length > 0);
};

/**
 * Collect the IDs of all disabled nodes in a team hierarchy.
 * Disabled teams (e.g. teams with courses but no learners) should not
 * trigger course fetching or appear as selected in report settings.
 *
 * @param treeData - The team hierarchy returned by `buildTeamHierarchy`
 * @returns A Set of disabled team IDs
 */
export const getDisabledTeamIds = (
  treeData: ReturnType<typeof buildTeamHierarchy>,
): Set<string> => {
  const disabledIds = new Set<string>();

  const collect = (nodes: ReturnType<typeof buildTeamHierarchy>) => {
    for (const node of nodes) {
      if (node.disabled) disabledIds.add(node._id);
      if (node.children) collect(node.children);
    }
  };

  collect(treeData);
  return disabledIds;
};
